import { GRADE_COMMENTARY, type GradeBand } from '../data/gradeCommentary';

export type Decoration = 'star' | 'check' | 'line';

export interface Grade {
  /** Whole-number percentage, 0–100. */
  pct: number;
  /** Display letter: 'A+', 'A', 'B', 'C', 'D', 'F', 'F-'. */
  letter: string;
  band: GradeBand;
  /** Which red marking sits under the commentary. */
  decoration: Decoration;
  commentary: string;
  /** Extra label shown only on the lowest grade's card. */
  tag?: string;
}

interface BandSpec {
  /** Inclusive lower bound (percentage) for this band. */
  min: number;
  letter: string;
  band: GradeBand;
  decoration: Decoration;
}

// Ordered high → low; first band whose `min` the score clears wins.
// Mirrors grades.txt + screen 07 exactly (see QUIZ_BUILD_PLAN.md grade map).
const BANDS: readonly BandSpec[] = [
  { min: 100, letter: 'A+', band: 'aPlus', decoration: 'star' },
  { min: 90, letter: 'A', band: 'a', decoration: 'star' },
  { min: 80, letter: 'B', band: 'b', decoration: 'check' },
  { min: 70, letter: 'C', band: 'c', decoration: 'line' },
  { min: 60, letter: 'D', band: 'd60', decoration: 'line' },
  { min: 40, letter: 'D', band: 'd4050', decoration: 'line' },
  { min: 10, letter: 'F', band: 'f1030', decoration: 'line' },
  { min: 0, letter: 'F-', band: 'fMinus', decoration: 'line' },
];

export function gradeFor(correct: number, total: number): Grade {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  const spec = BANDS.find((b) => pct >= b.min) ?? BANDS[BANDS.length - 1];
  return {
    pct,
    letter: spec.letter,
    band: spec.band,
    decoration: spec.decoration,
    commentary: GRADE_COMMENTARY[spec.band],
    tag: spec.band === 'fMinus' ? 'Academic Probation' : undefined,
  };
}

// Quiz art lives in /public/img/quiz/. Filenames match the renamed shape PNGs
// (circle-1…7, line-1…8, check-1, star-1…2). Picks are deterministic per
// episode so the picker gallery never repeats and every render of a given
// episode (results / preview / downloaded PNG) is identical.

/** Red circle around the letter grade — one fixed circle per episode (2→1 … 8→7). */
export function circleSrc(episode: number): string {
  const n = Math.min(Math.max(episode - 1, 1), 7);
  return `/img/quiz/circle-${n}.png`;
}

const DECORATION_COUNT: Record<Decoration, number> = { star: 2, check: 1, line: 7 };

/** Red marking under the commentary, deterministic per episode within its type. */
export function decorationSrc(decoration: Decoration, episode: number): string {
  const count = DECORATION_COUNT[decoration];
  const n = (episode % count) + 1;
  return `/img/quiz/${decoration}-${n}.png`;
}
